// ViewCollectionPage.jsx
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import "../styles/ViewCollectionPage.css";
import { getIdentifier } from "../utils/auth";
import TipPortal from "../components/TipPortal.js";
import AuthGateModal from "../components/AuthGateModal";

/* ───────────────── field order + labels + privacy map ─────────────────── */

const FIELD_ORDER = [
  "name",
  "artist",
  "dateCreated",
  "edition",
  "source",
  "memory",
  "material",
  "dimensions",
  "location",
  "condition",
  "price",
  "datePurchased",
  "notes",
  "links"
];

const LABELS = {
  name: "title",
  artist: "creator's name",
  dateCreated: "date created",
  edition: "edition number",
  source: "bought from",
  memory: "memory",
  location: "location",
  condition: "condition",
  price: "price",
  datePurchased: "date purchased",
  notes: "notes",
  material: "material",
  dimensions: "dimensions",
  links: "links"
};

const PRIVACY_MAP = {
  name: "name",
  artist: "artist",
  dateCreated: "dateCreated",
  edition: "edition",
  source: "source",
  memory: "memory",
  location: "location",
  condition: "condition",
  price: "price",
  datePurchased: "datePurchased",
  notes: "notes",
  material: "material",
  dimensions: "dimensions" 
};


// ✅ strict boolean privacy helper (prevents "false" string bugs)
const isPrivateTrue = (privacyObj, key) => privacyObj?.[key] === true;


function buildMetadata(item, viewMode) {
  if (!item) return { pairs: [], attachments: [] };

  const isFieldPrivate =
    viewMode === "public" && item?.privacy
      ? (key) => PRIVACY_MAP[key] && item.privacy[PRIVACY_MAP[key]]
      : () => false;

  const pairs = FIELD_ORDER
    .map((key) => {
      if (isFieldPrivate(key)) return null;
      const raw = item[key];
      if (raw === undefined || raw === null || raw === "") return null;

      let value;
      if (Array.isArray(raw)) {
  value = raw.join(", ");
}
else if (key === "links") {
  value = String(raw)
    .split("\n")
    .map(v => v.trim())
    .filter(Boolean);
}
else if (typeof raw === "object") {
  value = JSON.stringify(raw);
}
else {
  value = String(raw).trim();
}

      if (!value) return null;
      return { key, label: LABELS[key] || key, value };
    })
    .filter(Boolean);

  // ✅ Prefer presigned attachmentUrls (backend already generates these)
  const sourceList =
    Array.isArray(item.attachmentUrls) && item.attachmentUrls.length
      ? item.attachmentUrls
      : Array.isArray(item.attachments)
      ? item.attachments
      : [];

  const attachmentsRaw = sourceList
    .map((a, i) => {
      if (!a) return null;

      if (typeof a === "string") {
        const clean = a.split("?")[0];
        const name = clean.split("/").pop() || `Attachment ${i + 1}`;
        return { name, url: a };
      }

      if (a.url) {
        const clean = a.url.split("?")[0];
        const name = a.name || clean.split("/").pop() || `Attachment ${i + 1}`;
        return { name, url: a.url };
      }

      return null;
    })
    .filter(Boolean);

  const attachments =
    viewMode === "public" && item?.privacy?.attachment ? [] : attachmentsRaw;

  return { pairs, attachments };
}

/* ───────────────── helpers / api ───────────────── */

const API = "https://squid-app-6s2hc.ondigitalocean.app";
const ORIGIN = window.location.origin;

const postJSON = (url, body) =>
  fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(body)
  });

const idPayload = (identifier) =>
  identifier && identifier.includes("@")
    ? { email: identifier }
    : { username: identifier };

const parseTags = (raw) =>
  (raw || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

async function getSignedAttachmentUrl(att) {
  const identifier = getIdentifier();
  if (!identifier) return att.url;

  const res = await fetch(`${API}/sign-attachment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...idPayload(identifier),
      attachmentUrl: att.url,
    }),
  });

  const data = await res.json().catch(() => ({}));
  return data?.signedUrl || att.url;
}

/* ───────────────── component ───────────────── */

export default function ViewCollectionPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ supports routes like /#/p/:slug and /#/k/:key (plus legacy query links)
  const params = useParams();
  const routeSlug = params?.slug || null;
  const routeKey = params?.key || null;

  const isMobile = useMemo(
    () => window.matchMedia("(max-width: 900px)").matches,
    []
  );
  const selectedItemId =
    (location.state && location.state.selectedItemId) || null;

  // footer height -> css var
  const footerRef = useRef(null);
  useLayoutEffect(() => {
    const setH = () => {
      const h = footerRef.current?.offsetHeight || 56;
      document.documentElement.style.setProperty("--vc-footer-h", `${h}px`);
    };
    setH();
    window.addEventListener("resize", setH);
    window.addEventListener("orientationchange", setH);
    return () => {
      window.removeEventListener("resize", setH);
      window.removeEventListener("orientationchange", setH);
    };
  }, []);

  const [expandedMetadataId, setExpandedMetadataId] = useState(null);
  const [recipientUsername, setRecipientUsername] = useState("");
  const [ownerAllowCollectionContact, setOwnerAllowCollectionContact] = useState(true);
const [ownerDiscoverableProfile, setOwnerDiscoverableProfile] = useState(true);

  const prevShowThumbsRef = useRef(null);
  const prevInfoOpenRef = useRef(null);

  const sheetRef = useRef(null);
const startY = useRef(0);
const startTranslate = useRef(0);
const dragging = useRef(false);

const thumbRefs = useRef([]);

const SHEET_CLOSED = 62; // px visible when closed (peek)
const SHEET_OPEN = 0;

const getTranslateY = (el) => {
  const m = window.getComputedStyle(el).transform;
  if (!m || m === "none") return 0;
  const parts = m.match(/matrix.*\((.+)\)/);
  if (!parts) return 0;
  const values = parts[1].split(",").map((v) => Number(v.trim()));
  return values.length === 6 ? values[5] : 0;
};

const onSheetStart = (e) => {
  if (!isMobile) return;
  const sheet = sheetRef.current;
  if (!sheet) return;

  dragging.current = true;
  startY.current = e.touches[0].clientY;
  startTranslate.current = getTranslateY(sheet);

  sheet.style.transition = "none";
};

const onSheetMove = (e) => {
  if (!dragging.current) return;
  const sheet = sheetRef.current;
  if (!sheet) return;

  const y = e.touches[0].clientY;
  const delta = y - startY.current;

  // current translate + delta
  let next = startTranslate.current + delta;

  // clamp between open and closed positions
  next = Math.max(SHEET_OPEN, Math.min(SHEET_CLOSED, next));

  sheet.style.transform = `translateY(${next}px)`;
};

const onSheetEnd = () => {
  if (!dragging.current) return;
  dragging.current = false;

  const sheet = sheetRef.current;
  if (!sheet) return;

  sheet.style.transition = "";

  const cur = getTranslateY(sheet);

  // snap decision
  const shouldClose = cur > SHEET_CLOSED * 0.5;
  setInfoOpen(!shouldClose);

  // let CSS take over final snap
  sheet.style.transform = "";
};
  
  // legacy view mode helper (still supported)
  const getModeFromSearch = (search) =>
    new URLSearchParams(search).get("mode") === "public" ? "public" : "owner";

  // querystring params (legacy + filters)
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {

  setFilterQuery(qs.get("q") || "");

  setIncludedTags(
    parseTags(qs.get("tags"))
      .map(t => t.toLowerCase())
  );

  setExcludedTags(
    parseTags(qs.get("not"))
      .map(t => t.toLowerCase())
  );

}, [location.search]);

  // ✅ allow both: path params OR query params
  const qsSlug = qs.get("s") || null;
  const qsKey = qs.get("k") || null;

  // legacy fallback (name/username) — only used if slug/key missing
  const qsName = qs.get("c") || null;
  const qsUser = qs.get("u") || "";

  const effectiveSlug = routeSlug || qsSlug;
  const effectiveKey = routeKey || qsKey;

  const [viewMode, setViewMode] = useState(
    effectiveKey ? "owner" : effectiveSlug ? "public" : getModeFromSearch(location.search)
  );
  useEffect(() => {
    setViewMode(
      effectiveKey ? "owner" : effectiveSlug ? "public" : getModeFromSearch(location.search)
    );
  }, [location.search, effectiveKey, effectiveSlug]);

  // shared if you arrived via slug/key/name link
  const isSharedView = !!(effectiveSlug || qsName || effectiveKey);

  const identifier = useMemo(() => {
    try {
      return getIdentifier() || "";
    } catch {
      return "";
    }
  }, []);
  const isLoggedIn = !!identifier;

  const goHome = useCallback(() => {
    navigate(isLoggedIn ? "/home-page" : "/");
  }, [isLoggedIn, navigate]);

  // data
  const [collection, setCollection] = useState(null);

  useEffect(() => {
  if (!collection) return;

  document.title =
    `${collection.name} | the collectors platform`;

  const setMeta = (property, content, isName = false) => {
  const selector = isName
    ? `meta[name="${property}"]`
    : `meta[property="${property}"]`;

  let tag = document.querySelector(selector);

  if (!tag) {
    tag = document.createElement("meta");

    if (isName) {
      tag.setAttribute("name", property);
    } else {
      tag.setAttribute("property", property);
    }

    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
};

setMeta(
  "og:title",
  `${collection.name} | the collectors platform`
);

setMeta(
  "og:description",
  `${collection.name} collection on the collectors platform`
);

setMeta(
  "twitter:title",
  `${collection.name} | the collectors platform`,
  true
);

setMeta(
  "twitter:description",
  `${collection.name} collection on the collectors platform`,
  true
);

  let canonical = document.querySelector(
  'link[rel="canonical"]'
);

if (!canonical) {
  canonical = document.createElement("link");
  canonical.rel = "canonical";
  document.head.appendChild(canonical);
}

if (publicSlug) {
  canonical.href =
    `https://thecollectorsplatform.com/p/${publicSlug}`;
}

let schema = document.querySelector(
  'script[type="application/ld+json"]'
);

if (!schema) {
  schema = document.createElement("script");
  schema.type = "application/ld+json";
  document.head.appendChild(schema);
}

schema.textContent = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: collection.name,
  description: `${collection.name} collection`,
  url: canonical.href,
  keywords: availableTags.join(", ")
});

}, [collection]);

  const [ownerUsername, setOwnerUsername] = useState(qsUser || "");
  const [ownerShareKey, setOwnerShareKey] = useState(effectiveKey || null);

  // ✅ keep slug in state so share uses shortest link
  const [publicSlug, setPublicSlug] = useState(effectiveSlug || null);

  const [isPreloading, setIsPreloading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // UI state
  const [showThumbs, setShowThumbs] = useState(!isMobile);
  const [infoOpen, setInfoOpen] = useState(!isMobile);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // paging
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imgIndex, setImgIndex] = useState(0);

  const [signedAtt, setSignedAtt] = useState({});
  const [attPreview, setAttPreview] = useState({
    open: false,
    url: "",
    name: "",
    kind: "",
    loading: false,
  });

  // filters
  const [filterQuery, setFilterQuery] = useState(qs.get("q") || "");
  const [includedTags, setIncludedTags] = useState(
    parseTags(qs.get("tags")).map((t) => t.toLowerCase())
  );
  const [excludedTags, setExcludedTags] = useState(
    parseTags(qs.get("not")).map((t) => t.toLowerCase())
  );

  const clearFilters = useCallback(() => {
    setFilterQuery("");
    setIncludedTags([]);
    setExcludedTags([]);
  }, []);

  const filtersActive =
    !!filterQuery.trim() || includedTags.length > 0 || excludedTags.length > 0;

  const toggleViewMode = () => {
    const next = viewMode === "owner" ? "public" : "owner";
    setViewMode(next);

    // only meaningful for legacy name-based share; slug is always public, key always owner
    if (isSharedView) {
      const p = new URLSearchParams(location.search);
      p.set("mode", next);
      navigate(`${location.pathname}?${p.toString()}`, {
        replace: true,
        state: location.state,
      });
    }
  };

  function cycleTag(tag) {
    const t = tag.toLowerCase();
    if (includedTags.includes(t)) {
      setIncludedTags((p) => p.filter((x) => x !== t));
      setExcludedTags((p) => (p.includes(t) ? p : [...p, t]));
      return;
    }
    if (excludedTags.includes(t)) {
      setExcludedTags((p) => p.filter((x) => x !== t));
      return;
    }
    setIncludedTags((p) => [...p, t]);
  }

  const isImg = (url = "") =>
    /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(url.split("?")[0]);
  const isPdf = (url = "") => /\.pdf$/i.test(url.split("?")[0]);

  const searchableText = (it, mode) => {
    const isPub = mode === "public";
    const p = it.privacy || {};
    const pick = (key, privKey = key) =>
      isPub && p[privKey] ? "" : it[key] || "";
    return [
      pick("name"),
      pick("artist"),
      pick("memory"),
      pick("notes"),
      pick("source", "source"),
      pick("location", "location"),
      pick("tags", "tags"),
    ]
      .join(" ")
      .toLowerCase();
  };

    /* auth gate modal */
    const [modalOpen, setModalOpen] = useState(false);
    const [nextPath, setNextPath] = useState("/");
    const [nextState, setNextState] = useState(undefined);

  // view-all
  const [viewAll, setViewAll] = useState(() => isMobile);
  const MIN_TILE = 200;
  const MAX_TILE = 340;
  const STEP = 20;
  const clampTile = (v) => Math.max(MIN_TILE, Math.min(MAX_TILE, v));
  const [viewAllTile, setViewAllTile] = useState(() => {
    const raw = Number(localStorage.getItem("viewAllTile"));
    return clampTile(Number.isFinite(raw) && raw ? raw : 280);
  });
  useEffect(() => {
    localStorage.setItem("viewAllTile", String(viewAllTile));
  }, [viewAllTile]);
  const atMin = viewAllTile <= MIN_TILE;
  const atMax = viewAllTile >= MAX_TILE;
  const percent = ((viewAllTile - MIN_TILE) / (MAX_TILE - MIN_TILE)) * 100;


  const [contactOpen, setContactOpen] = useState(false);

  const [message, setMessage] = useState("");
const [sending, setSending] = useState(false);
const [sent, setSent] = useState(false);

const sendMessage = async () => {

  if (!message.trim()) return;

  setSending(true);

  try {

    const res = await fetch(
      `${API}/send-message`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
      body: JSON.stringify({
  recipientUsername: ownerUsername,
  message,
  email: getIdentifier()
})
      }
    );

    const data = await res.json();


   if (data.success) {

  setSent(true);
  setMessage("");

  setTimeout(() => {

    setContactOpen(false);
    setSent(false);

  }, 1200);

}

  } catch (err) {

    console.error(err);
    alert("Request failed");

  }

  setSending(false);

};

useEffect(() => {

  if (location.state?.openContact) {

    if (location.state.ownerUsername) {

      setOwnerUsername(location.state.ownerUsername);
      setRecipientUsername(location.state.ownerUsername);

    }

    setContactOpen(true);

    navigate(location.pathname + location.search, {
      replace: true,
      state: {
        ...location.state,
        openContact: false
      }
    });

  }

}, [
  location.state,
  location.pathname,
  location.search,
  navigate
]);


  // share modal
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMode, setShareMode] = useState("public");
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);


  const [keyLoading, setKeyLoading] = useState(false);
  const [slugLoading, setSlugLoading] = useState(false);

  const [qrOpen, setQrOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const linkRef = useRef(null);

  const [shareIncludeFilters, setShareIncludeFilters] = useState(false);
  const [shareQ, setShareQ] = useState("");
  const [shareTags, setShareTags] = useState([]);
  const [shareNot, setShareNot] = useState([]);
  const [noFilterMsg, setNoFilterMsg] = useState(false);

  useEffect(() => {
    if (!shareOpen) return;
    const any =
      !!filterQuery.trim() || includedTags.length > 0 || excludedTags.length > 0;

    setShareIncludeFilters(any);
    setShareQ(filterQuery);
    setShareTags(includedTags);
    setShareNot(excludedTags);
  }, [shareOpen, filterQuery, includedTags, excludedTags]);

  const ensureShareKey = async () => {
    if (ownerShareKey) return ownerShareKey;
    const id = getIdentifier();
    const name = (collection?.name || qsName || "").trim();
    if (!id || !name) return null;

    const res = await postJSON(`${API}/create-share-key`, {
      ...idPayload(id),
      collectionName: name,
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data?.key) {
      setOwnerShareKey(data.key);
      return data.key;
    }
    return null;
  };

  const ensurePublicSlug = async () => {
    if (publicSlug) return publicSlug;

    const id = getIdentifier();
    const name = (collection?.name || qsName || "").trim();
    if (!id || !name) return null;

    if (collection?.collectionPrivate) return null;

    const res = await postJSON(`${API}/create-public-slug`, {
      ...idPayload(id),
      collectionName: name,
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data?.slug) {
      setPublicSlug(data.slug);
      return data.slug;
    }
    return null;
  };

  // ✅ shortest links:
  // public: /#/p/<slug>
  // owner:  /#/k/<key>
  // (filters can append ?q=...&tags=... etc)
  const makeShareLinks = ({ withFilters = false } = {}) => {
    const addFilters = (params) => {
      if (!withFilters) return params;

      const q = (shareQ || "").trim();
      if (q) params.set("q", q);

      const tags = Array.isArray(shareTags) ? shareTags.filter(Boolean) : [];
      const nots = Array.isArray(shareNot) ? shareNot.filter(Boolean) : [];

      if (tags.length) params.set("tags", tags.join(","));
      if (nots.length) params.set("not", nots.join(","));

      return params;
    };

    const filterParams = addFilters(new URLSearchParams());
    const tail = filterParams.toString();
    const suffix = tail ? `?${tail}` : "";

    const slug = publicSlug || effectiveSlug;
    const key = ownerShareKey || effectiveKey;

    return {
      public: slug ? `${ORIGIN}/p/${slug}${suffix}` : "",
      owner: key ? `${ORIGIN}/k/${key}${suffix}` : "",
    };
  };

  const collectionNameFromState = location.state?.collectionName || null;
  const nameForEdit = collection?.name ?? collectionNameFromState ?? null;

  const openShare = async () => {
    setShareMode(collection?.collectionPrivate ? "owner" : "public");
    setShareMenuOpen(false);
    setCopied(false);

    // if collection is public, create/get slug so link is short
    if (!collection?.collectionPrivate && !(publicSlug || effectiveSlug)) {
      setSlugLoading(true);
      try {
        await ensurePublicSlug();
      } finally {
        setSlugLoading(false);
      }
    }

    if (collection?.collectionPrivate && !ownerShareKey) {
      setKeyLoading(true);
      try {
        await ensureShareKey();
      } finally {
        setKeyLoading(false);
      }
    }

    setShareOpen(true);
  };

  const closeShare = () => {
    setShareOpen(false);
    setShareMenuOpen(false);
    setCopied(false);
    setQrOpen(false);
  };

const downloadPDF = async () => {
  const doc = new jsPDF();

  // helper
  const imageToDataURL = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();

    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  doc.setFontSize(20);
  doc.text(collection.name || "Collection", 20, 20);

  let y = 40;

  for (const item of items) {

    // page break
    if (y > 220) {
      doc.addPage();
      y = 20;
    }

    // image
    const imageUrl =
      item.previewUrl ||
      item.fileUrl ||
      item.originalUrl;

    if (imageUrl) {
      try {
        const imgData = await imageToDataURL(imageUrl);

       const format =
  imageUrl.toLowerCase().includes(".png")
    ? "PNG"
    : "JPEG";

doc.addImage(
  imgData,
  format,
  20,
  y,
  60,
  60
);

        y += 70;
      } catch (err) {
        console.log("Image load failed:", err);
      }
    }

    // title
    doc.setFontSize(16);
    doc.text(item.name || "Untitled", 20, y);
    y += 10;

    // metadata
    const { pairs } = buildMetadata(item, "owner");

    doc.setFontSize(11);

    pairs.forEach(({ label, value }) => {

      const lines = doc.splitTextToSize(
        `${label}: ${value}`,
        160
      );

      doc.text(lines, 20, y);

      y += lines.length * 6 + 2;
    });

    // divider
    y += 8;
    doc.line(20, y, 190, y);
    y += 12;
  }

  doc.save(`${collection.name}.pdf`);
};

  const copyCurrentLink = async () => {
    if (shareMode === "owner" && !(ownerShareKey || effectiveKey)) {
      setKeyLoading(true);
      try {
        await ensureShareKey();
      } finally {
        setKeyLoading(false);
      }
    }

    if (
      shareMode === "public" &&
      !collection?.collectionPrivate &&
      !(publicSlug || effectiveSlug)
    ) {
      setSlugLoading(true);
      try {
        await ensurePublicSlug();
      } finally {
        setSlugLoading(false);
      }
    }

    const out = makeShareLinks({ withFilters: shareIncludeFilters })[shareMode];
    if (!out) return;

    try {
      await navigator.clipboard.writeText(out);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      linkRef.current?.select?.();
    } catch {
      // no-op
    }
  };

  const openQrForCurrentLink = async () => {
    try {
      const url = makeShareLinks({ withFilters: shareIncludeFilters })[shareMode];
      if (!url) return;
      const dataUrl = await QRCode.toDataURL(url, {
        width: 720,
        margin: 1,
        color: { dark: "#000000", light: "#ffffff" },
      });
      setQrDataUrl(dataUrl);
      setQrOpen(true);
    } catch {
      // no-op
    }
  };

  // load + preload
  const [progress, setProgress] = useState({
    active: false,
    percent: 0,
    label: "loading…",
  });

  const preloadImagesWithProgress = async (urls, onTick) => {
    const list = Array.isArray(urls) ? urls.filter(Boolean) : [];
    if (list.length === 0) {
      onTick?.(1, 1);
      return;
    }
    const total = list.length;
    let done = 0;

    const loadOne = (src) =>
      new Promise((resolve) => {
        const img = new Image();
        img.src = src;
        const finish = () => {
          done++;
          onTick?.(done, total);
          resolve();
        };
        img.onload = finish;
        img.onerror = finish;
      });

    await Promise.allSettled(list.map(loadOne));
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsPreloading(true);
      setLoadError("");

      try {
        const s = effectiveSlug;
        const k = effectiveKey;

        // legacy fallback
        const mode = qs.get("mode") === "owner" ? "owner" : "public";
        const c = qs.get("c");
        const u = qs.get("u") || "";

        let res, data;

        if (k) {
          res = await postJSON(`${API}/get-collection-by-key`, { key: k });
          data = await res.json();
          if (!res.ok) throw new Error(data?.error || "Failed to load");

          setOwnerUsername((data?.owner?.username || "").trim());
        
        setOwnerAllowCollectionContact(
          data?.owner?.allowCollectionContact !== false
        );

        setOwnerDiscoverableProfile(
          data?.owner?.discoverableProfile !== false
        );
          setOwnerShareKey(k);

        } else if (s) {

  res = await postJSON(
    `${API}/get-collection-public-by-slug`,
    { slug: s }
  );

  data = await res.json();

  if (!res.ok)
    throw new Error(data?.error || "Failed to load");

  setPublicSlug(s);

  setOwnerUsername(
    (data?.owner?.username || "").trim()
  );

  setOwnerAllowCollectionContact(
    data?.owner?.allowCollectionContact !== false
  );

  setOwnerDiscoverableProfile(
    data?.owner?.discoverableProfile !== false
  );
}
         else if (c && mode === "public") {
          res = await postJSON(`${API}/get-collection-public`, {
            collectionName: c,
            username: u || undefined,
          });
          data = await res.json();
          if (!res.ok) throw new Error(data?.error || "Failed to load");
        } else {
          const collectionNameFromStateInner = location.state?.collectionName || null;
          const id = getIdentifier();
          if (!collectionNameFromStateInner || !id) throw new Error("Nothing to load");
          res = await postJSON(`${API}/get-collection`, {
            ...idPayload(id),
            collectionName: collectionNameFromStateInner,
          });
          data = await res.json();
          if (!res.ok) throw new Error(data?.error || "Failed to load");
        }

        const coll = data.collection;

        const items = coll?.items || [];
        const urls = items
          .flatMap((it) => [
            it.previewUrl || it.fileUrl || it.originalUrl,
            ...(Array.isArray(it.additionalImages)
              ? it.additionalImages.map((img) => img.previewUrl || img.fileUrl || img.originalUrl)
              : []),
          ])
          .filter(Boolean);

        setProgress({ active: true, percent: 0, label: "loading items…" });
       
        if (!cancelled) {
          setCollection(coll);
          setIsPreloading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(typeof e?.message === "string" ? e.message : "Failed to fetch");
          setIsPreloading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state, effectiveSlug, effectiveKey]);

  // visible items
  const itemsAll = collection?.items || [];
  const visibleItems = useMemo(() => {
    if (!collection) return [];
    if (viewMode === "owner") return itemsAll;
    if (collection.collectionPrivate) return [];
    return itemsAll.filter((it) => it.public !== false);
  }, [collection, itemsAll, viewMode]);

  // tags
  const availableTags = useMemo(() => {
    const src = visibleItems;
    const bag = new Set();
    src.forEach((it) => {
      const raw = it.tags || "";
      raw.split(",").forEach((t) => {
        const tag = t.trim();
        if (tag) bag.add(tag);
      });
    });
    return Array.from(bag).sort((a, b) => a.localeCompare(b));
  }, [visibleItems]);

  useEffect(() => {
  if (!collection) return;

  const tagSet = new Set();

  visibleItems.forEach((item) => {
    (item.tags || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .forEach((t) => tagSet.add(t));

    if (item.artist) tagSet.add(item.artist);
    if (item.material) tagSet.add(item.material);
    if (item.dimensions) tagSet.add(item.dimensions);
  });

  let keywords = document.querySelector(
    'meta[name="keywords"]'
  );

  if (!keywords) {
    keywords = document.createElement("meta");
    keywords.name = "keywords";
    document.head.appendChild(keywords);
  }

  keywords.content = [...tagSet].join(", ");
}, [visibleItems, collection]);


useEffect(() => {
  if (!collection) return;

  const topTags = availableTags
    .slice(0, 15)
    .join(", ");

  let description = document.querySelector(
    'meta[name="description"]'
  );

  if (!description) {
    description = document.createElement("meta");
    description.name = "description";
    document.head.appendChild(description);
  }

  const artists = [
  ...new Set(
    visibleItems
      .map(item => item.artist)
      .filter(Boolean)
  )
]
.slice(0,5)
.join(", ");

description.content =
`${collection.name}. Collection featuring ${artists}${artists ? ", " : ""}${topTags}.`;

}, [collection, availableTags]);

  // apply filters
  const items = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    const hasQ = q.length > 0;
    const hasIncl = includedTags.length > 0;
    const hasExcl = excludedTags.length > 0;
    if (!hasQ && !hasIncl && !hasExcl) return visibleItems;

    return visibleItems.filter((it) => {
      const textOk = !hasQ || searchableText(it, viewMode).includes(q);

      const itemTags = (it.tags || "")
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);

      const tagsOk =
        (!hasIncl || includedTags.every((t) => itemTags.includes(t))) &&
        (!hasExcl || excludedTags.every((t) => !itemTags.includes(t)));

      return textOk && tagsOk;
    });
  }, [visibleItems, filterQuery, includedTags, excludedTags, viewMode]);

  const hasItemsForSale = useMemo(
  () => items.some((item) => item.sale),
  [items]
);

  // keep indexes sane
  useEffect(() => {
    if (!items.length) {
      setCurrentIndex(0);
      setImgIndex(0);
      return;
    }
    if (currentIndex >= items.length) setCurrentIndex(0);
  }, [items.length, currentIndex]);

  // reset sub-image when item changes
  useEffect(() => {
  thumbRefs.current[currentIndex]?.scrollIntoView({
    behavior: "smooth",
    block: "nearest",
  });
}, [currentIndex]);

  // jump to item from Discover
  useEffect(() => {
    if (!selectedItemId || !items.length) return;
    const idx = items.findIndex(
      (it) => it.id === selectedItemId || selectedItemId.endsWith(`::${it.id}`)
    );
    if (idx >= 0) {
      setCurrentIndex(idx);
      setImgIndex(0);
    }
  }, [selectedItemId, items]);

  const goNext = useCallback(() => {
    if (!items.length) return;
    setCurrentIndex((p) => (p + 1 >= items.length ? 0 : p + 1));
  }, [items.length]);

  const goPrev = useCallback(() => {
    if (!items.length) return;
    setCurrentIndex((p) => (p - 1 < 0 ? items.length - 1 : p - 1));
  }, [items.length]);

  const guessKind = (url = "") => {
    if (isImg(url)) return "image";
    if (isPdf(url)) return "pdf";
    return "file";
  };

  const closeAttachmentPreview = useCallback(() => {
    setAttPreview({ open: false, url: "", name: "", kind: "", loading: false });
  }, []);
  const openAttachmentPreview = useCallback(async () => {}, []);

  useEffect(() => {
    if (viewAll) {
      if (prevShowThumbsRef.current === null) prevShowThumbsRef.current = showThumbs;
      if (prevInfoOpenRef.current === null) prevInfoOpenRef.current = infoOpen;

      setShowThumbs(false);
      setInfoOpen(false);
    } else {
      if (prevShowThumbsRef.current !== null) {
        setShowThumbs(prevShowThumbsRef.current);
        prevShowThumbsRef.current = null;
      }
      if (prevInfoOpenRef.current !== null) {
        setInfoOpen(prevInfoOpenRef.current);
        prevInfoOpenRef.current = null;
      }

      // ✅ mobile: default to showing metadata under the image
      if (isMobile) setInfoOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAll]);

  // keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") goNext();
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") goPrev();
      if (e.key === "Escape") {
        setViewAll(false);
        setFiltersOpen(false);
        setShareOpen(false);
        setQrOpen(false);
        closeAttachmentPreview();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, closeAttachmentPreview]);

  // early exits
  if (isPreloading) {
    return (
      <div className="vc-loading">
        {progress.active ? `${progress.label} ${progress.percent}%` : "loading collection…"}
      </div>
    );
  }
  if (loadError) return <div className="vc-loading">{loadError}</div>;
  if (!collection) return <div className="vc-loading">collection not found.</div>;
  if (viewMode === "public" && items.length === 0 && !filtersActive) {
    return <div className="vc-loading">no public items to display.</div>;
  }

  const hasResults = items.length > 0;
  const currentItem = hasResults ? items[currentIndex] : null;

  const allImages = (() => {
    if (!currentItem) return [];
    const mainUrl = currentItem.previewUrl || currentItem.fileUrl || currentItem.originalUrl;
    const main = mainUrl ? { url: mainUrl } : null;

    const extras = (Array.isArray(currentItem.additionalImages) ? currentItem.additionalImages : [])
      .map((img) => ({ url: img.previewUrl || img.fileUrl || img.originalUrl }))
      .filter((i) => !!i.url);

    return [main, ...extras].filter(Boolean);
  })();

  const curSubImg = allImages[imgIndex] || allImages[0] || {};
  const { pairs, attachments } = buildMetadata(currentItem, viewMode);

  const hasMeta = pairs.length > 0 || attachments.length > 0;

  const publicDisabled = !!collection?.collectionPrivate;
  const viewLabel =
  isSharedView
    ? "public view"
    : viewMode === "owner"
    ? "private view"
    : "public view";

  const canEdit = !!(nameForEdit && isLoggedIn && !isSharedView);
  const canEditNow = canEdit && !viewAll;

  const goToEdit = () => {
    if (!canEdit) return;
    navigate("/edit-collection", {
      state: {
        collectionName: nameForEdit,
        selectedItemId: currentItem?.id || null,
        selectedSubImageIndex: imgIndex,
      },
    });
  };


  const titleValue = (currentItem?.name || "untitled").trim();
  const artistValue = (currentItem?.artist || "").trim();
  const metaPairs = pairs.filter((p) => p.key !== "name" && p.key !== "artist");

  const hasAnyImage = hasResults && allImages.length > 0 && !!curSubImg.url;

  const shareBusy = keyLoading || slugLoading;

  return (
    <div className="vc-page">
      {/* HEADER */}
      <header className="vc-header">
        <div className="vc-head-left">
          <div className="vc-left-controls" role="group" aria-label="left controls">
            {!isSharedView && (
              <TipPortal
                text={
                  publicDisabled
                    ? "private collection (no public view)"
                    : viewMode === "owner"
                    ? "switch to public view"
                    : "switch to private view"
                }
                side="bottom"
              >
                <button
                  type="button"
                  className={`vc-btn ${viewMode === "owner" ? "is-on" : ""}`}
                  onClick={publicDisabled ? undefined : toggleViewMode}
                  disabled={publicDisabled}
                  aria-disabled={publicDisabled}
                >
                  {viewMode === "owner" ? "🌐" : "🧑‍💻"}
                </button>
              </TipPortal>

            )}

            {!viewAll && !isMobile && items.length > 1 && (
              <TipPortal text={showThumbs ? "hide thumbnails" : "show thumbnails"} side="bottom">
                <button
                  type="button"
                  className={`vc-btn ${showThumbs ? "is-on" : ""}`}
                  onClick={() => setShowThumbs((p) => !p)}
                  aria-pressed={showThumbs}
                >
                  {showThumbs ? "🙈" : "🙉"}
                </button>
              </TipPortal>
            )}
          </div>

          <div className="vc-title">
            <h1 className="vc-h1">{(collection?.name || qsName || "collection").trim()}</h1>
            <div className="vc-sub">
  <span>{collection.collectionPrivate ? "🔐 private" : "🔓 public"}</span>
  <span className="vc-dot">·</span>
  <span>{items.length} items</span>
 
   {ownerUsername && ownerDiscoverableProfile && (
  <>
    <span className="vc-dot">·</span>

    <span
      className="vc-owner-link"
      onClick={() => {

        if (isLoggedIn) {

          navigate(`/u/${ownerUsername}`);

        } else {

         setNextPath(
  `/u/${ownerUsername}`
);

setNextState(undefined);

          setModalOpen(true);

        }

      }}
    >
      @{ownerUsername}
    </span>
  </>
)}

  {!isMobile && !isSharedView && (
    <>
      <span className="vc-dot">·</span>
      <span className="vc-badge">{viewLabel}</span>
    </>
  )}

  {isMobile && !isSharedView && (
  <div className="vc-view-label-row">
    <span className="vc-badge">{viewLabel}</span>
  </div>
)}

</div>

          </div>
        </div>

        <div className="vc-actions" role="group" aria-label="controls">
          {viewAll && !isMobile && (
            <div className="tcp-zoom" role="group" aria-label="thumbnail size">
              <button
                type="button"
                className="tcp-zoom-btn"
                onClick={() => setViewAllTile((v) => clampTile(v - STEP))}
                disabled={atMin}
                aria-label="smaller thumbnails"
                title="smaller"
              >
                −
              </button>

              <input
                className="tcp-zoom-slider"
                type="range"
                min={MIN_TILE}
                max={MAX_TILE}
                step={STEP}
                value={viewAllTile}
                onChange={(e) => setViewAllTile(clampTile(Number(e.target.value)))}
                style={{ "--fill": `${percent}%` }}
                aria-label="thumbnail size slider"
              />

              <button
                type="button"
                className="tcp-zoom-btn"
                onClick={() => setViewAllTile((v) => clampTile(v + STEP))}
                disabled={atMax}
                aria-label="larger thumbnails"
                title="larger"
              >
                +
              </button>
            </div>
          )}

          {hasMeta && (
  <TipPortal text={filtersOpen ? "hide filters" : "filter items"} side="bottom">
    <button
      type="button"
      className={`vc-btn ${filtersOpen ? "is-on" : ""}`}
      onClick={() => setFiltersOpen((p) => !p)}
      aria-pressed={filtersOpen}
    >
      ☕️
      {filtersActive && <span className="vc-dot-indicator" aria-hidden="true" />}
    </button>
  </TipPortal>
)}

 {!isSharedView && (
  <TipPortal text="share collection" side="bottom">
    <button type="button" className="vc-btn" onClick={openShare}>
      📤
    </button>
  </TipPortal>
)}

 {isSharedView && ownerAllowCollectionContact && (
  <TipPortal text="contact collector" side="bottom">
    <button
      type="button"
      className="vc-btn"
      onClick={() => setContactOpen(true)}
    >
      📧
    </button>
  </TipPortal>
)}
          {items.length > 0 && !isMobile && (
            <TipPortal text={viewAll ? "exit view all" : "view all items"} side="bottom">
              <button
                type="button"
                className={`vc-btn ${viewAll ? "is-on" : ""}`}
                onClick={() => setViewAll((p) => !p)}
                aria-pressed={viewAll}
              >
                ◼
              </button>
            </TipPortal>
          )}

      {hasMeta && !isMobile && (
  <TipPortal
    text={
      viewAll
        ? "exit view all to see item details"
        : infoOpen
        ? "hide item details"
        : "show item details"
    }
    side="bottom"
  >
    <button
      type="button"
      className={`vc-btn ${infoOpen ? "is-on" : ""}`}
      onClick={() => !viewAll && setInfoOpen((p) => !p)}
      aria-pressed={infoOpen}
      disabled={viewAll}
    >
      💬
    </button>
  </TipPortal>
)}

        
              
{!isSharedView && !isMobile && (
  <TipPortal text={viewAll ? "click on the item" : "edit item"} side="bottom">
    <button
      type="button"
      className={`vc-btn ${canEditNow ? "" : "is-disabled"}`}
      onClick={() => !viewAll && goToEdit()}
      disabled={!canEditNow || viewAll}
    >
      ✏️
    </button>
  </TipPortal>
)}

          <TipPortal text="home" side="bottom">
            <button type="button" className="vc-btn" onClick={goHome} title="home">
              🏠
            </button>
          </TipPortal>
        </div>
      </header>

      {/* FILTER PANEL */}
        <section className={`vc-filters ${filtersOpen ? "open" : ""}`} aria-label="filters">
         <div className="vc-filter-row">
  <input
    className="vc-filter-input"
    type="search"
    value={filterQuery}
    onChange={(e) => setFilterQuery(e.target.value)}
    placeholder="filter by value"
    aria-label="filter by value"
    spellCheck={false}
  />

  {filtersActive && (
    <button
      type="button"
      className="vc-filter-clear"
      onClick={clearFilters}
      aria-label="clear filters"
      title="clear filters"
    >
      clear
    </button>
  )}
</div>

          {availableTags.length > 0 && (
            <div className="vc-tag-grid" role="list" aria-label="tags">
              {availableTags.map((tag) => {
                const t = tag.toLowerCase();
                const inIncl = includedTags.includes(t);
                const inExcl = excludedTags.includes(t);
                return (
                  <button
                    key={tag}
                    type="button"
                    className={`vc-tag ${inIncl ? "on" : inExcl ? "not" : ""}`}
                    onClick={() => cycleTag(tag)}
                    aria-pressed={inIncl || inExcl}
                    role="listitem"
                  >
                    {inExcl ? `− ${tag}` : inIncl ? `+ ${tag}` : tag}
                  </button>
                );
              })}
            </div>
          )}
           {!viewAll && (
<div className="vc-filter-tip">
  press <strong>esc</strong> to close filters
  <br />
  click tags to include, double click to exclude
</div>
)}
        </section>


      {/* BODY */}
      <div className={`vc-body ${infoOpen && hasMeta ? "has-info" : ""}`}>
        {!viewAll && showThumbs && items.length > 1 && (
          <aside className="vc-thumbs" aria-label="thumbnails">
            {items.map((it, idx) => (
             <button
  key={it.id || idx}
  ref={(el) => (thumbRefs.current[idx] = el)}
  type="button"
  className={`vc-thumb ${idx === currentIndex ? "active" : ""}`}
                onClick={() => {
                  setCurrentIndex(idx);
                  setImgIndex(0);
                }}
                aria-label={`go to item ${idx + 1}`}
              >
                <img
                  src={it.previewUrl || it.fileUrl || it.originalUrl || "/placeholder.png"}
                  alt={it.name || `thumbnail ${idx + 1}`}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.currentTarget.src = "/placeholder.png";
                  }}
                />
              </button>
            ))}
          </aside>
        )}

        {viewAll ? (
          <section className="vc-viewall" aria-label="view all items">
            <div
              className="vc-viewall-grid"
              style={{
                gridTemplateColumns: `repeat(auto-fill, minmax(${viewAllTile}px, 1fr))`,
              }}
            >
              {items.map((it, idx) => {

  const itemMeta = buildMetadata(it, viewMode);

  const hasItemMeta =
    itemMeta.pairs.length > 0 ||
    itemMeta.attachments.length > 0;

  return (
        
                <button
                  key={it.id || idx}
                  type="button"
                  className="vc-viewall-card"
                  onClick={() => {
                    if (isMobile) return;

                    setCurrentIndex(idx);
                    setImgIndex(0);
                    setViewAll(false);
                  }}
                  aria-label={`open item ${idx + 1}`}
                >
                  <img
                    src={it.previewUrl || it.fileUrl || it.originalUrl || "/placeholder.png"}
                    alt={it.name || `item ${idx + 1}`}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => (e.currentTarget.src = "/placeholder.png")}
                  />
                  <div className="vc-viewall-meta">

  <div className="vc-viewall-title-row">
    <div className="vc-viewall-title">
      {(it.name || "untitled").trim()}
    </div>

    <div className="vc-viewall-actions">
      {it.sale && (
  <div className="vc-sale-indicator">
  🏷 for sale
</div>
)}

      {!isSharedView && (
        <button
          className="vc-viewall-action-btn"
          onClick={(e) => {
            e.stopPropagation();

            navigate("/edit-collection", {
              state: {
                collectionName: nameForEdit,
                selectedItemId: it.id
              }
            });
          }}
        >
          ✏️
        </button>
      )}

{hasItemMeta && isMobile && (
      <button
  className="vc-viewall-action-btn"
  onClick={(e) => {

    e.stopPropagation();

    setExpandedMetadataId(
      expandedMetadataId === it.id
        ? null
        : it.id
    );

  }}
>
  {
    expandedMetadataId === it.id
      ? "💬"
      : "💬"
  }
</button>
)}

    </div>
  
  </div>

</div>
{expandedMetadataId === it.id && (
  <div className="vc-viewall-metadata">

    {buildMetadata(it, viewMode).pairs.map(
      ({ key, label, value }) => (

        <div
          key={key}
          className="vc-viewall-meta-row"
        >
          <div className="vc-viewall-meta-label">
            {label}
          </div>

          <div className="vc-viewall-meta-value">
            {value}
          </div>
        </div>

      )
    )}

  </div>
)}
               </button>
  );
})}

{!isMobile && ( 
<div className="vc-filter-tip">
  press <strong>esc</strong> to close filters. 
</div>
)}
            </div>
          </section>
        ) : (
          <main className="vc-stage" aria-label="viewer">

         <div className="vc-image-frame">
  <img
    className="vc-image"
    src={curSubImg.url}
    alt={currentItem?.altText || currentItem?.name || "collection image"}
    decoding="async"
    draggable={false}
  />

  {allImages.length > 1 && (
    <>
      <button
        type="button"
        className="vc-img-arrow vc-img-prev"
        onClick={() =>
          setImgIndex(
            imgIndex === 0
              ? allImages.length - 1
              : imgIndex - 1
          )
        }
      >
        ◀
      </button>

      <button
        type="button"
        className="vc-img-arrow vc-img-next"
        onClick={() =>
          setImgIndex(
            imgIndex === allImages.length - 1
              ? 0
              : imgIndex + 1
          )
        }
      >
        ▶
      </button>
    </>
  )}

</div>


         {isMobile && hasResults && hasMeta && (
  <section className="vc-mobile-meta">
    <div className="vc-row">
      <div className="vc-k">title</div>
      <div className="vc-v">{titleValue}</div>
    </div>

    {!!artistValue && (
      <div className="vc-row">
        <div className="vc-k">creator's name</div>
        <div className="vc-v">{artistValue}</div>
      </div>
    )}

    {metaPairs.map(({ key, label, value }) => (
      <div key={key} className="vc-row">
        <div className="vc-k">{label}</div>
          <div
  className={`vc-v ${
    key === "memory" || key === "notes"
      ? "vc-v-long"
      : ""
  }`}
>
  {key === "links" ? (
    <div className="vc-links">
      {value.map((link, i) => (
       <a
  key={i}
  href={
    link.startsWith("http://") ||
    link.startsWith("https://")
      ? link
      : `https://${link}`
  }
  target="_blank"
  rel="noopener noreferrer"
  className="vc-link"
>
  {link}
</a>
      ))}
    </div>
  ) : (
    value
  )}
</div>
        </div>
    ))}

    {attachments.length > 0 && (
      <div className="vc-row">
        <div className="vc-k">attachments</div>
        <div className="vc-v vc-attachments">
          {/* copy your existing attachment map here */}
        </div>
      </div>
    )}

  </section>
)}
          </main>
        )}

    {hasResults && hasMeta && !isMobile && (
  <aside
    id="vc-info-panel"
    ref={sheetRef}
    className={`vc-info ${infoOpen ? "open" : ""}`}
    aria-label="item info"
>

            <div
  className="vc-info-grab"
  onTouchStart={onSheetStart}
  onTouchMove={onSheetMove}
  onTouchEnd={onSheetEnd}
/>

            <div className="vc-info-body">
              {currentItem?.sale && (
  <div className="vc-sale-row">
    🏷️ available for purchase
  </div>
)}
              <div className="vc-row">
                <div className="vc-k">title</div>
                <div className="vc-v">{titleValue}</div>
              </div>

              {!!artistValue && (
                <div className="vc-row">
                  <div className="vc-k">creator&apos;s name</div>
                  <div className="vc-v">{artistValue}</div>
                </div>
              )}

              {metaPairs.map(({ key, label, value }) => (
                <div key={key} className="vc-row">
                  <div className="vc-k">{label}</div>
                  <div className={`vc-v ${key === "memory" || key === "notes" ? "vc-v-long" : ""}`}>
                    <div
  className={`vc-v ${
    key === "memory" || key === "notes"
      ? "vc-v-long"
      : ""
  }`}
>
  {key === "links" ? (
    <div className="vc-links">
      {value.map((link, i) => (
        <a
  key={i}
  href={
    link.startsWith("http://") ||
    link.startsWith("https://")
      ? link
      : `https://${link}`
  }
  target="_blank"
  rel="noopener noreferrer"
  className="vc-link"
>
  {link}
</a>
      ))}
    </div>
  ) : (
    value
  )}
</div>
                  </div>
                </div>
              ))}

              {attachments.length > 0 && (
                <div className="vc-row">
                  <div className="vc-k">attachments</div>
                  <div className="vc-v vc-attachments">
                    {attachments.map((att, i) => {
                      const url = att.url || "";
                      const name = att.name || `Attachment ${i + 1}`;
                      return (
                        <div key={`${url}-${i}`} className="vc-attrow">
                          <div className="vc-attthumb" aria-hidden="true">
                            {isImg(url) ? (
                              <img
                                src={signedAtt[url] || url}
                                alt=""
                                loading="lazy"
                                decoding="async"
                                onError={(e) => (e.currentTarget.style.display = "none")}
                              />
                            ) : (
                              <span className="vc-atticon">{isPdf(url) ? "PDF" : "FILE"}</span>
                            )}
                          </div>

                          <div className="vc-atttext">
                            <div className="vc-attname" title={name}>
                              {name}
                            </div>
                          </div>

                          <a
                            className="vc-attopen"
                            href={signedAtt[url] || url}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            title="open/download"
                          >
                            open ↗
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </aside>
        )}
      </div>

      {!viewAll && items.length > 1 && (
        <footer className="vc-footer" ref={footerRef}>
          <button type="button" className="vc-nav" onClick={goPrev}>
            ←
          </button>
       <span className="vc-count">
  {items.length ? currentIndex + 1 : 0} / {items.length}
</span>

{!isMobile && (
  <div className="vc-viewall-tip">
    press ← → ↑ ↓ keys to navigate
  </div>
)}
          <button type="button" className="vc-nav" onClick={goNext}>
          →
          </button>
        </footer>
      )}

      {/* SHARE MODAL */}
      {shareOpen && (
        <div className="vc-modal-overlay" role="dialog" aria-modal="true" onClick={closeShare}>
          <div className="vc-modal" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeShare} aria-label="Close" className="vc-modal-close">
              ×
            </button>

            <p className="vc-modal-title">
              share <strong>"{collection?.name || qsName}"</strong>
            </p>

            <div
  className={`vc-share-row ${
    collection?.collectionPrivate ? "vc-share-row-private" : ""
  }`}
>
              <input
                ref={linkRef}
                type="text"
                readOnly
                value={makeShareLinks({ withFilters: shareIncludeFilters })[shareMode]}
                onFocus={(e) => e.target.select()}
                className="vc-share-input"
              />

              {!collection?.collectionPrivate && (
                <div className="vc-share-select">
                  <button
                    type="button"
                    onClick={() => setShareMenuOpen((o) => !o)}
                    aria-haspopup="listbox"
                    aria-expanded={shareMenuOpen}
                    className="vc-share-select-btn"
                  >
                    {shareMode === "public" ? "public view" : "private view"}{" "}
                    <span className="vc-caret">▾</span>
                  </button>

                  {shareMenuOpen && (
                    <div role="listbox" className="vc-share-menu">
                      {[
                        { key: "public", label: "public view" },
                        { key: "owner", label: "private view" },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          role="option"
                          aria-selected={shareMode === opt.key}
                          className="vc-share-menu-item"
                          onClick={async () => {
                            if (opt.key === "owner" && !(ownerShareKey || effectiveKey)) {
                              setKeyLoading(true);
                              try {
                                await ensureShareKey();
                              } finally {
                                setKeyLoading(false);
                              }
                            }

                            if (
                              opt.key === "public" &&
                              !collection?.collectionPrivate &&
                              !(publicSlug || effectiveSlug)
                            ) {
                              setSlugLoading(true);
                              try {
                                await ensurePublicSlug();
                              } finally {
                                setSlugLoading(false);
                              }
                            }

                            setShareMode(opt.key);
                            setShareMenuOpen(false);
                            setCopied(false);
                          }}
                        >
                          {shareMode === opt.key ? "✓ " : ""}
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button
                type="button"
                className="vc-share-copy"
                onClick={copyCurrentLink}
                disabled={shareBusy}
              >
                {shareBusy ? "loading…" : copied ? "copied ✓" : "copy link"}
              </button>
              <button
              type="button"
              className="vc-share-qr"
              onClick={downloadPDF}
            >
              📄 download PDF
            </button>
            </div>

            <div style={{ marginTop: 12 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  justifyContent: "center",
                  color: "rgba(230,230,225,0.75)",
                  fontSize: "0.95rem",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={shareIncludeFilters}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setShareIncludeFilters(on);

                    if (on) {
                      setShareQ(filterQuery);
                      setShareTags(includedTags);
                      setShareNot(excludedTags);

                      const none =
                        !filterQuery.trim() &&
                        includedTags.length === 0 &&
                        excludedTags.length === 0;

                      if (none) {
                        setNoFilterMsg(true);
                        setTimeout(() => setNoFilterMsg(false), 1200);
                      }
                    }
                  }}
                />
                <span>include current filters</span>
              </label>

              {noFilterMsg && (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    marginTop: 10,
                    textAlign: "center",
                    color: "rgba(0,255,65,0.9)",
                    fontSize: "0.9rem",
                  }}
                >
                  no filters selected
                </div>
              )}
            </div>

            {qrOpen && (
              <div className="vc-qr">
                <div className="vc-qr-img">
                  <img src={qrDataUrl} alt="QR code" />
                </div>
                <div className="vc-qr-actions">
                  <a className="vc-qr-btn" href={qrDataUrl} download="collection-qr.png">
                    download
                  </a>
                  <button className="vc-qr-btn" type="button" onClick={() => setQrOpen(false)}>
                    close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {contactOpen && (

  isLoggedIn ? (

    <div
      className="vc-modal-overlay"
      onClick={() => setContactOpen(false)}
    >
      <div
        className="vc-modal"
        onClick={(e) => e.stopPropagation()}
      >

        <button
          className="vc-modal-close"
          onClick={() => setContactOpen(false)}
        >
          ×
        </button>

        <p className="vc-modal-title">
          contact collector
        </p>

        <div className="vc-contact-form">

          <textarea
            className="vc-share-input"
            rows={8}
            placeholder="insert your message here"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />

          <button
            className="vc-share-copy"
            onClick={sendMessage}
            disabled={sending}
          >
            {sending
              ? "sending..."
              : sent
              ? "sent ✓"
              : "send"}
          </button>

        </div>

      </div>
    </div>

  ) : (

    <AuthGateModal
      isOpen={true}
      onClose={() => setContactOpen(false)}
    nextPath={location.pathname + location.search}
nextState={{
    ...(location.state || {}),
    openContact: true,
    ownerUsername
}}
      heading="contact this collector"
      subtext="sign up or log in to send a message"
    />

  )

)}
  
      {/* ATTACHMENT PREVIEW MODAL (kept, but will never open from clicks now) */}
      {attPreview.open && (
        <div className="vc-modal-overlay" role="dialog" aria-modal="true" onClick={closeAttachmentPreview}>
          <div className="vc-modal vc-att-modal" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeAttachmentPreview} aria-label="Close" className="vc-modal-close">
              ×
            </button>

            <p className="vc-modal-title">
              preview <strong>"{attPreview.name}"</strong>
            </p>

            <div className="vc-att-preview">
              {attPreview.loading ? (
                <div className="vc-loading" style={{ position: "relative", inset: "auto" }}>
                  loading…
                </div>
              ) : guessKind(attPreview.url) === "image" ? (
                <img className="vc-att-preview-img" src={attPreview.url} alt={attPreview.name} draggable={false} />
              ) : guessKind(attPreview.url) === "pdf" ? (
                <iframe className="vc-att-preview-frame" title={attPreview.name} src={attPreview.url} />
              ) : (
                <div className="vc-att-preview-file">
                  <p style={{ color: "rgba(230,230,225,0.75)" }}>This file can’t be previewed here.</p>
                  <a className="vc-qr-btn" href={attPreview.url} target="_blank" rel="noopener noreferrer">
                    open in new tab ↗
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {viewMode === "public" && availableTags.length > 0 && (
  <section className="vc-seo-tags">

    <div className="vc-seo-title">
      tags
    </div>

    <div className="vc-seo-list">
      {availableTags.map(tag => (
        <span key={tag} className="vc-seo-tag">
          {tag}
        </span>
      ))}
    </div>

  </section>
)}
<AuthGateModal
  isOpen={modalOpen}
  onClose={() => setModalOpen(false)}
  nextPath={nextPath}
  nextState={nextState}
  heading="view collector profile"
  subtext="sign up or log in to view this collector's profile"
/>
    </div>
  );
}